import React from "react";
import { useTranslation } from "react-i18next";

import {
  AlertTriangle,
  ArrowLeftRight,
  Ban,
  Banknote,
  BarChart3,
  Bell,
  Ruler,
  Building2,
  Calculator,
  Calendar,
  CalendarCheck,
  ClipboardList,
  Clock,
  CreditCard,
  DoorOpen,
  FileText,
  Handshake,
  Hotel,
  Landmark,
  Layers,
  LayoutDashboard,
  MapPin,
  Monitor,
  Package,
  PackageCheck,
  Plug,
  Receipt,
  RotateCcw,
  Settings,
  Shield,
  History,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Tag,
  Ticket,
  UserCheck,
  UserCircle,
  UserCog,
  Users,
  Utensils,
  UtensilsCrossed,
  Factory,
  Wallet,
  Globe,
  GitBranch,
  BarChart2,
  QrCode,
  UserRoundCheck,
  MessageSquareText,
  MonitorPlay,
  PartyPopper,
  ChefHat,
  Gift,
} from "lucide-react";

import { useAuth } from "@/context/auth.context";
import { useLocation } from "react-router-dom";
import { useOrganization } from "@/context/organization.context";

import { useModules } from "@/context/modules.context";
import usePermissions from "./use-permissions";
import useDomain from "./use-domain";
import { useIntegrations } from "@/context/integrations.context";
import useSettings from "./useSettings";
import { app_env, env_config } from "@/env-config";

export function useSidebarLinks() {
  const { t } = useTranslation("sidebar");
  const { user } = useAuth();

  const pathname = useLocation().pathname;

  const { organizations = [], role, activeOrg } = useOrganization();

  const { enabledModules, isEnabled } = useModules();

  const [search, setSearch] = React.useState("");

  const { canPerform } = usePermissions();

  const { orgDomain } = useDomain(env_config[app_env].domain);

  const { isIntegrationEnabled } = useIntegrations();

  const { settings } = useSettings();

  const isRestaurantOrderCompletionPaymentEnabled =
    settings?.enable_restaurant_order_completion_payments !== false &&
    settings?.enable_restaurant_order_completion_payments !== "false";

  // Restaurant discounts are opt-out: on unless explicitly disabled.
  const isRestaurantDiscountsEnabled =
    settings?.enable_automatic_discounts !== false &&
    settings?.enable_automatic_discounts !== "false";

  const isInternalTransfersEnabled =
    settings?.enable_internal_transfers !== false &&
    settings?.enable_internal_transfers !== "false";

  const isSalesReturnsEnabled =
    settings?.enable_sales_returns !== false &&
    settings?.enable_sales_returns !== "false";

  // The kitchen display is opt-out: on unless explicitly disabled
  // (hooks/useKdsEnabled.ts explains why the default is on).
  const isKdsEnabled =
    settings?.enable_restaurant_kds !== false &&
    settings?.enable_restaurant_kds !== "false";

  // Menu recipes are opt-in: off unless explicitly enabled
  // (hooks/useMenuRecipesEnabled.ts explains why the default is off).
  const isMenuRecipesEnabled =
    settings?.enable_menu_recipes === true ||
    settings?.enable_menu_recipes === "true";

  // The loyalty programme is opt-in: off unless explicitly enabled
  // (hooks/useLoyaltyEnabled.ts explains why the default is off).
  const isLoyaltyEnabled =
    settings?.enable_restaurant_loyalty === true ||
    settings?.enable_restaurant_loyalty === "true";

  const baseData = React.useMemo(() => {
    return {
      user: {
        name: user?.name,
        email: user?.email,
        profileUrl: user?.profileUrl,
        role: role?.name,
      },
      teams: !orgDomain
        ? organizations.map((e) => {
            return {
              id: e.id,
              name: e.name,
              plan: "Enterprise",
            };
          })
        : [
            {
              id: activeOrg?.id,
              name: activeOrg?.name,
              plan: "Enterprise",
            },
          ],
      // Every module the member can open, unfiltered by the search box.
      allNavMain: [
        {
          title: t("modules.sales.title"),
          url: "#",
          icon: ShoppingBag,
          isActive: pathname.startsWith("/sales"),
          module: "sales",
          items: [
            {
              title: t("modules.sales.items.overview"),
              url: "/sales/overview",
              icon: LayoutDashboard,
              view: canPerform("access_sales_dashboard"),
            },
            {
              title: t("modules.sales.items.orders"),
              url: "/sales/list",
              icon: ShoppingCart,
              view: canPerform("view_sales"),
            },
            {
              title: t("modules.sales.items.pos"),
              url: "/sales/pos",
              icon: Monitor,
              view: canPerform("access_pos"),
              isExternal: true,
            },
            {
              title: t("modules.sales.items.onlineOrders"),
              url: "/sales/online-orders",
              icon: Globe,
              view:
                canPerform("view_sales") &&
                settings?.enable_online_orders === true,
            },
            {
              title: t("modules.sales.items.returns"),
              url: "/sales/sales-returns",
              icon: RotateCcw,
              view: canPerform("view_sales_returns") && isSalesReturnsEnabled,
            },
            {
              title: t("modules.sales.items.quotations"),
              url: "/sales/quotations",
              icon: FileText,
              view:
                canPerform("view_quotations") &&
                settings?.enable_quotations === true,
            },
            {
              title: t("modules.sales.items.transactions"),
              url: "/sales/transactions",
              icon: CreditCard,
              view: canPerform("view_payments_transactions"),
            },
            {
              title: t("modules.sales.items.unclaimedQrPayments"),
              url: "/sales/qr-payments",
              icon: QrCode,
              // Gated by `enable_pos_qr_payments` (off by default). The route
              // itself stays reachable so money paid before the switch went
              // off can still be dealt with by direct link.
              view:
                canPerform("view_qr_payment_requests") &&
                (settings?.enable_pos_qr_payments === true ||
                  settings?.enable_pos_qr_payments === "true"),
            },
            {
              title: t("modules.sales.items.outletShifts"),
              url: "/sales/outlet-shifts",
              icon: Clock,
              view:
                canPerform("view_outlet_shifts") &&
                settings?.enable_outlet_shifts === true,
            },
            {
              title: t("modules.sales.items.products"),
              url: "/sales/products",
              icon: Package,
              view: canPerform("view_products"),
            },
            {
              // All RRA/EBM (VSDC) screens live under one collapsed group so
              // the sales menu is not dominated by tax-authority links.
              title: t("modules.sales.items.ebm"),
              url: "/sales/ebm-imports",
              icon: Landmark,
              view:
                isIntegrationEnabled("vsdc") &&
                (canPerform("view_ebm_imports") ||
                  canPerform("view_ebm_purchases") ||
                  canPerform("view_ebm_notices")),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.sales.subItems.ebmImports"),
                  url: "/sales/ebm-imports",
                  view: canPerform("view_ebm_imports"),
                },
                {
                  title: t("modules.sales.subItems.ebmPurchases"),
                  url: "/sales/ebm-purchases",
                  view: canPerform("view_ebm_purchases"),
                },
                {
                  title: t("modules.sales.subItems.ebmNotices"),
                  url: "/sales/ebm-notices",
                  view: canPerform("view_ebm_notices"),
                },
              ].filter((e) => e.view),
            },
            {
              // Claims, payments and payer setup are all insurance back-office
              // work, so they sit together (docs/pharmacy-insurance-plan.md §4).
              title: t("modules.sales.items.insuranceManagement"),
              url: "/sales/insurance/claims",
              icon: FileText,
              view:
                (canPerform("view_insurance_claims") ||
                  canPerform("view_insurers") ||
                  canPerform("manage_insurance_price_lists")) &&
                (settings?.insurance_enabled === true ||
                  settings?.insurance_enabled === "true"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.sales.subItems.claims"),
                  url: "/sales/insurance/claims",
                  view: canPerform("view_insurance_claims"),
                },
                {
                  title: t("modules.sales.subItems.insurancePayments"),
                  url: "/sales/insurance/payments",
                  view: canPerform("view_insurance_claims"),
                },
                {
                  title: t("modules.sales.subItems.insurers"),
                  url: "/sales/insurance/insurers",
                  view: canPerform("view_insurers"),
                },
                {
                  title: t("modules.sales.subItems.insurancePriceLists"),
                  url: "/sales/insurance/price-lists",
                  view: canPerform("manage_insurance_price_lists"),
                },
              ].filter((e) => e.view),
            },
            {
              title: t("modules.sales.items.reports"),
              url: "/sales/reports",
              icon: BarChart3,
              view: canPerform("access_sales_reports"),
            },
            {
              title: t("modules.sales.items.settings"),
              url: "/sales/settings",
              icon: Settings,
              view: canPerform("access_sales_settings"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.sales.subItems.generalSettings"),
                  url: "/sales/settings",
                  view: canPerform("manage_general_sales_settings"),
                },
                {
                  title: t("modules.sales.subItems.customers"),
                  url: "/sales/settings/customers",
                  view: canPerform("view_customers"),
                },
                {
                  title: t("modules.sales.subItems.printBarcodes"),
                  url: "/sales/settings/print-barcodes",
                  // Gated by `enable_barcode_printing` (server/src/data/
                  // barcodeSettings.ts). Defaults to on, so an organization
                  // that never opens settings keeps the entry it has today.
                  view:
                    canPerform("print_product_barcodes") &&
                    settings?.enable_barcode_printing !== false &&
                    settings?.enable_barcode_printing !== "false",
                },
                {
                  title: t("modules.sales.subItems.categories"),
                  url: "/sales/settings/categories",
                  view: canPerform("view_product_categories"),
                },

                {
                  title: t("modules.sales.subItems.outlets"),
                  url: "/sales/settings/outlets",
                  view: canPerform("view_pos_outlets"),
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.inventory.title"),
          url: "#",
          icon: Layers,
          isActive: pathname.startsWith("/inventory"),
          module: "inventory",
          items: [
            {
              title: t("modules.inventory.items.overview"),
              url: "/inventory/overview",
              icon: LayoutDashboard,
              view: canPerform("access_inventory_dashboard"),
            },
            {
              title: t("modules.inventory.items.locations"),
              url: "/inventory/locations",
              icon: MapPin,
              view: canPerform("view_warehouses"),
            },
            {
              title: t("modules.inventory.items.requisitions"),
              url: "/inventory/requisitions",
              icon: ClipboardList,
              view:
                canPerform("view_requisitions") &&
                settings?.enable_requisition_orders === true,
            },
            {
              title: t("modules.inventory.items.purchases"),
              url: "/inventory/purchases",
              icon: FileText,
              view: canPerform("view_purchase_orders"),
            },
            {
              title: t("modules.inventory.items.receiving"),
              url: "/inventory/purchase-receiving",
              icon: PackageCheck,
              view:
                canPerform("view_purchase_receiving") &&
                settings?.enable_purchase_receiving === true,
            },
            {
              title: t("modules.inventory.items.transfers"),
              url: "/inventory/internal-transfers",
              icon: ArrowLeftRight,
              view:
                canPerform("view_internal_transfers") &&
                isInternalTransfersEnabled,
            },
            {
              title: t("modules.inventory.items.stockCounting"),
              url: "/inventory/stock-counting",
              icon: Calculator,
              view:
                canPerform("view_stock_countings") &&
                settings?.enable_stock_counting === true,
            },
            {
              // Recipes are inventory reference data, but they only mean
              // anything to a kitchen — hidden unless the restaurant module
              // is on, rather than shown empty to a hardware shop.
              title: t("modules.inventory.items.recipes"),
              url: "/inventory/recipes",
              icon: ChefHat,
              view:
                canPerform("view_menu_recipes") &&
                isEnabled("restaurant") &&
                isMenuRecipesEnabled,
            },
            {
              title: t("modules.inventory.items.adjustments"),
              url: "/inventory/stock-adjustments",
              icon: SlidersHorizontal,
              view: canPerform("view_manual_adjustments"),
            },
            {
              title: t("modules.inventory.items.batches"),
              url: "/inventory/batches",
              icon: Layers,
              view:
                canPerform("view_batches") &&
                (settings?.enable_batch_tracking === true ||
                  settings?.enable_batch_tracking === "true"),
            },
            {
              title: t("modules.inventory.items.supplierInvoices"),
              url: "/inventory/invoices",
              icon: Receipt,
              view:
                canPerform("view_supplier_invoices") &&
                settings?.enable_supplier_invoices === true,
            },
            {
              title: t("modules.inventory.items.supplierPayments"),
              url: "/inventory/purchase-payments",
              icon: Wallet,
              view:
                canPerform("view_supplier_payments") &&
                settings?.enable_supplier_invoices === true,
            },
            {
              title: t("modules.inventory.items.reports"),
              url: "/inventory/reports",
              icon: BarChart3,
              view: canPerform("access_inventory_reports"),
            },
            {
              title: t("modules.inventory.items.settings"),
              url: "/inventory/settings",
              icon: Settings,
              view: canPerform("access_inventory_settings"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.inventory.subItems.generalSettings"),
                  url: "/inventory/settings",
                  view: canPerform("manage_general_inventory_settings"),
                },
                {
                  title: t("modules.inventory.subItems.suppliers"),
                  url: "/inventory/settings/suppliers",
                  view: canPerform("view_suppliers"),
                },
                {
                  title: t("modules.inventory.subItems.stockItems"),
                  url: "/inventory/settings/stock-items",
                  view: canPerform("view_stock_items"),
                },
                {
                  title: t("modules.inventory.subItems.stockCategories"),
                  url: "/inventory/settings/stock-item-categories",
                  view: canPerform("view_stock_item_categories"),
                },
                {
                  title: t("modules.inventory.subItems.unitConversions"),
                  url: "/inventory/settings/convertions",
                  view: canPerform("view_convertions"),
                },

                {
                  title: t("modules.inventory.subItems.liquidProfiles"),
                  url: "/inventory/settings/liquid-profiles",
                  view:
                    canPerform("manage_liquid_profiles") &&
                    settings?.enable_liquid_profiles === true,
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.manufacturing.title"),
          url: "#",
          icon: Factory,
          isActive: pathname.startsWith("/manufacturing"),
          module: "manufacturing",
          items: [
            {
              title: t("modules.manufacturing.items.overview"),
              url: "/manufacturing/overview",
              icon: LayoutDashboard,
              view: canPerform("access_manufacturing_dashboard"),
            },
            {
              title: t("modules.manufacturing.items.boms"),
              url: "/manufacturing/boms",
              icon: ClipboardList,
              view: canPerform("view_boms"),
            },
            {
              title: t("modules.manufacturing.items.productionOrders"),
              url: "/manufacturing/production-orders",
              icon: Factory,
              view: canPerform("view_production_orders"),
            },
            {
              title: t("modules.manufacturing.items.materialRequests"),
              url: "/manufacturing/material-requests",
              icon: PackageCheck,
              view: canPerform("view_material_requests"),
            },
            {
              title: t("modules.manufacturing.items.batches"),
              url: "/manufacturing/batches",
              icon: Layers,
              view: canPerform("view_batches"),
            },
            {
              title: "Reports & Analytics",
              url: "/manufacturing/reports",
              icon: BarChart3,
              view: canPerform("access_manufacturing_reports"),
            },
            {
              title: "Settings & Configurations",
              url: "/manufacturing/settings",
              icon: Settings,
              view: canPerform("access_manufacturing_settings"),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.finance.title"),
          url: "#",
          icon: Landmark,
          isActive: pathname.startsWith("/accounting"),
          module: "finance",
          items: [
            {
              title: t("modules.finance.items.staffPortal"),
              url: "/financial-management/staff-portal",
              icon: UserCircle,
              view: settings?.enable_employee_portal ?? true,
            },
            {
              title: t("modules.finance.items.expenses"),
              url: "/financial-management/expenses",
              icon: Receipt,
              view: canPerform("view_expenses"),
            },
            {
              title: t("modules.finance.items.digitalWallet"),
              url: "/financial-management/digital-wallet",
              icon: Wallet,
              view:
                canPerform("view_digital_wallet") &&
                settings?.enable_digital_wallet === true,
            },
            {
              title: t("modules.finance.items.businessAccounts"),
              url: "/financial-management/business-accounts",
              icon: Banknote,
              view:
                canPerform("view_business_accounts") &&
                (settings?.enable_business_accounts ?? true),
            },
            {
              title: t("modules.finance.items.customerCredits"),
              url: "/financial-management/customer-credits",
              icon: UserCheck,
              view: canPerform("view_customer_credits"),
            },
            {
              // The hotel city ledger, for the accounts team. Only a property
              // running the hotel module has one, so the item hides entirely
              // rather than leading to an empty page.
              title: t("modules.finance.items.hotelCredits"),
              url: "/financial-management/hotel-credits",
              icon: Landmark,
              view: isEnabled("hotel") && canPerform("view_hotel_credits"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.finance.subItems.hotelCreditsLedger"),
                  url: "/financial-management/hotel-credits",
                  view: canPerform("view_hotel_credits"),
                },
                {
                  title: t("modules.finance.subItems.hotelCreditPayments"),
                  url: "/financial-management/hotel-credits/payments",
                  view: canPerform("view_hotel_credits"),
                },
              ].filter((e) => e.view),
            },
            {
              title: t("modules.finance.items.customerDeposits"),
              url: "/financial-management/customer-deposits",
              icon: Wallet,
              view:
                canPerform("view_customer_deposits") &&
                settings?.enable_customer_deposits === true,
            },
            {
              title: t("modules.finance.items.employeeCredits"),
              url: "/financial-management/employee-credits",
              icon: Users,
              view: canPerform("view_employee_credits"),
            },

            {
              title: t("modules.finance.items.employeeSanctions"),
              url: "/financial-management/employee-sanctions",
              icon: AlertTriangle,
              view:
                canPerform("view_sanctions") &&
                settings?.enable_sanctions === true,
            },

            {
              title: "Employee Loans",
              url: "/financial-management/employee-loans",
              icon: Wallet,
              view:
                canPerform("view_employee_loans") &&
                (settings?.enable_employee_loans ?? true),
            },

            {
              title: t("modules.finance.items.creditPayments"),
              url: "/financial-management/credit-payments",
              icon: Wallet,
              view: canPerform("view_credit_payments"),
            },

            {
              title: t("modules.finance.items.memberPayrolls"),
              url: "/financial-management/member-payrolls",
              icon: Wallet,
              view:
                canPerform("view_member_payrolls") &&
                settings?.enable_salary_configurations === true,
            },
            {
              title: t("modules.finance.items.reports"),
              url: "/financial-management/reports",
              icon: BarChart3,
              view: canPerform("access_financial_reports"),
            },
            {
              title: t("modules.finance.items.settings"),
              url: "/financial-management/settings",
              icon: Settings,
              view: canPerform("access_financial_settings"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.finance.subItems.generalSettings"),
                  url: "/financial-management/settings",
                  view: canPerform("manage_general_financial_settings"),
                },
                {
                  title: t("modules.finance.subItems.expenseCategories"),
                  url: "/financial-management/settings/expense-categories",
                  view: canPerform("view_expenses_categories"),
                },
                {
                  title: t("modules.finance.subItems.sanctionTypes"),
                  url: "/financial-management/settings/sanction-types",
                  view:
                    canPerform("view_sanction_types") &&
                    settings?.enable_sanctions === true,
                },
                {
                  title: t("modules.finance.subItems.salaryConfigs"),
                  url: "/financial-management/settings/salary-configs",
                  view:
                    canPerform("view_member_salary_configs") &&
                    settings?.enable_salary_configurations === true,
                },
                {
                  title: t("modules.finance.subItems.staffDocuments"),
                  url: "/financial-management/settings/staff-documents",
                  view:
                    canPerform("view_staff_documents") &&
                    (settings?.enable_staff_documents ?? true),
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.crm.title"),
          url: "#",
          icon: Handshake,
          isActive: pathname.startsWith("/crm"),
          module: "crm",
          items: [
            {
              title: t("modules.crm.items.dashboard"),
              url: "/crm/dashboard",
              icon: LayoutDashboard,
              view:
                canPerform("view_crm_manager_dashboard") ||
                canPerform("view_crm_my_dashboard"),
            },
            {
              title: t("modules.crm.items.leadsList"),
              url: "/crm/leads",
              icon: Users,
              view: canPerform("view_crm_leads"),
            },
            {
              title: t("modules.crm.items.activityLog"),
              url: "/crm/activity-log",
              icon: ClipboardList,
              view: canPerform("view_crm_activity_logs"),
            },
            // {
            //   title: t("modules.crm.items.followUps"),
            //   url: "/crm/follow-ups",
            //   icon: CalendarClock,
            //   view: canPerform("view_crm_follow_ups"),
            // },
            {
              title: t("modules.crm.items.dealsPipeline"),
              url: "/crm/deals-pipeline",
              icon: GitBranch,
              view: canPerform("view_crm_deals_pipeline"),
            },
            {
              title: t("modules.crm.items.performance"),
              url: "/crm/performance",
              icon: BarChart2,
              view: canPerform("view_crm_performance"),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.restaurant.title"),
          url: "#",
          icon: Utensils,
          isActive: pathname.startsWith("/restaurant"),
          module: "restaurant",
          items: [
            {
              title: t("modules.restaurant.items.overview"),
              url: "/restaurant/overview",
              icon: LayoutDashboard,
              view: canPerform("access_restaurant_dashboard"),
            },
            {
              title: t("modules.restaurant.items.pos"),
              url: "/restaurant/pos",
              icon: Monitor,
              view: canPerform("can_access_restaurant_pos"),
              isExternal: app_env === "table-tronic" ? false : true,
            },
            {
              title: t("modules.restaurant.items.kds"),
              url: "/restaurant/kds",
              icon: ChefHat,
              view: isKdsEnabled && canPerform("can_access_kds"),
              isExternal: app_env === "table-tronic" ? false : true,
            },
            {
              title: t("modules.restaurant.items.orders"),
              url: "/restaurant/orders",
              icon: ShoppingCart,
              view: canPerform("view_restaurant_orders"),
            },
            {
              title: t("modules.restaurant.items.tickets"),
              url: "/restaurant/order-tickets",
              icon: Ticket,
              view: canPerform("view_restaurant_order_tickets"),
            },
            {
              title: t("modules.restaurant.items.salesItems"),
              url: "/restaurant/sales-items",
              icon: Tag,
              view: canPerform("view_restaurant_sales_items"),
            },
            // qrcode-orders
            {
              title: "QR-code Orders",
              url: "/restaurant/online-orders",
              icon: QrCode,
              view:
                canPerform("view_restaurant_online_orders") &&
                settings?.enable_restaurant_scan_to_order === true,
            },
            {
              title: t("modules.restaurant.items.transactions"),
              url: "/restaurant/transactions",
              icon: CreditCard,
              view:
                canPerform("view_restaurant_payments_transactions") &&
                isRestaurantOrderCompletionPaymentEnabled,
            },
            {
              title: t("modules.restaurant.items.menuItems"),
              url: "/restaurant/menu-items",
              icon: UtensilsCrossed,
              view: canPerform("view_menu_items"),
            },
            {
              title: t("modules.restaurant.items.availabilityAlerts"),
              url: "/restaurant/availability-alerts",
              icon: Ban,
              view: canPerform("view_menu_availability_alerts"),
            },
            {
              title: t("modules.restaurant.items.loyalty", {
                defaultValue: "Loyalty program",
              }),
              url: "/restaurant/loyalty",
              icon: Gift,
              view: isLoyaltyEnabled && canPerform("view_restaurant_loyalty"),
            },
            {
              title: t("modules.restaurant.items.workPeriods"),
              url: "/restaurant/work-periods",
              icon: Clock,
              view: canPerform("view_work_periods"),
            },

            {
              title: t("modules.restaurant.items.ebmBills"),
              url: "/restaurant/ebm-bills",
              icon: Receipt,
              view:
                isIntegrationEnabled("vsdc") &&
                canPerform("view_restaurant_ebm_bills"),
            },
            {
              title: t("modules.restaurant.items.ebmPurchases"),
              url: "/restaurant/ebm-purchases",
              icon: ShoppingCart,
              view:
                isIntegrationEnabled("vsdc") &&
                canPerform("view_restaurant_ebm_purchases"),
            },
            {
              title: t("modules.restaurant.items.reports"),
              url: "/restaurant/reports",
              icon: BarChart3,
              view: canPerform("access_restaurant_reports"),
            },
            {
              title: t("modules.restaurant.items.settings"),
              url: "/restaurant/settings",
              icon: Settings,
              view: canPerform("access_restaurant_settings"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.restaurant.subItems.generalSettings"),
                  url: "/restaurant/settings",
                  view: canPerform("manage_general_restaurant_settings"),
                },

                {
                  title: t("modules.restaurant.subItems.customers"),
                  url: "/restaurant/settings/customers",
                  view: canPerform("view_restaurant_customers"),
                },
                {
                  title: t("modules.restaurant.subItems.stations"),
                  url: "/restaurant/settings/stations",
                  view: canPerform("view_order_stations"),
                },
                {
                  title: t("modules.restaurant.subItems.categories"),
                  url: "/restaurant/settings/categories",
                  view: canPerform("view_menu_categories"),
                },

                {
                  title: t("modules.restaurant.subItems.tables"),
                  url: "/restaurant/settings/tables",
                  view: canPerform("view_tables"),
                },
                {
                  title: t("modules.restaurant.subItems.discounts"),
                  url: "/restaurant/settings/discounts",
                  view:
                    isRestaurantDiscountsEnabled &&
                    canPerform("view_restaurant_discounts"),
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.hotel.title"),
          url: "#",
          icon: Hotel,
          isActive: pathname.startsWith("/hotel"),
          module: "hotel",
          items: [
            {
              title: t("modules.hotel.items.overview"),
              url: "/hotel/overview",
              icon: LayoutDashboard,
              view: canPerform("access_hotel_dashboard"),
            },
            {
              title: t("modules.hotel.items.availability"),
              url: "/hotel/availability",
              icon: Calendar,
              view: canPerform("view_hotel_calendar"),
            },
            {
              title: t("modules.hotel.items.bookings"),
              url: "/hotel/bookings",
              icon: CalendarCheck,
              view: canPerform("view_bookings"),
            },
            {
              // Booking quotations. Sits directly under Bookings: quoting and
              // booking are the same job at two moments, and a coordinator
              // moves between them all day.
              //
              // Gated by `hotel_quotations_enabled` as well as by permission —
              // a property that only takes walk-ins should not carry a sales
              // pipeline it never opens.
              title: t("modules.hotel.items.quotations"),
              url: "/hotel/quotations",
              icon: FileText,
              view:
                canPerform("view_booking_quotations") &&
                (settings?.hotel_quotations_enabled === true ||
                  settings?.hotel_quotations_enabled === "true"),
            },
            {
              title: t("modules.hotel.items.guests"),
              url: "/hotel/guests",
              icon: Users,
              view: canPerform("view_guests"),
            },
            {
              title: t("modules.hotel.items.roomAvailability"),
              url: "/hotel/room-availability",
              icon: DoorOpen,
              view: canPerform("view_rooms"),
            },
            {
              title: t("modules.hotel.items.services"),
              url: "/hotel/services",
              icon: Bell,
              view: canPerform("view_services"),
            },
            {
              title: t("modules.hotel.items.payments"),
              url: "/hotel/payments",
              icon: CreditCard,
              view: canPerform("view_hotel_payments"),
            },
            {
              title: t("modules.hotel.items.onlineBookings"),
              url: "/hotel/online-bookings",
              icon: Globe,
              view: canPerform("view_hotel_online_bookings"),
            },
            {
              title: t("modules.hotel.items.folios"),
              url: "/hotel/folios",
              icon: Receipt,
              view: canPerform("view_bookings"),
            },
            {
              title: t("modules.hotel.items.ratePlans"),
              url: "/hotel/rate-plans",
              icon: Tag,
              view: canPerform("view_room_availability"),
            },
            {
              // Housekeeping. Gated by `hotel_housekeeping_enabled` as well as
              // by permission. The switch defaults **on** — housekeeping
              // shipped before it existed, so only an explicit `false` hides
              // the entry; the API 403s the whole surface in that case.
              title: t("modules.hotel.items.housekeeping"),
              url: "/hotel/housekeeping",
              icon: ClipboardList,
              view:
                canPerform("access_housekeeping_dashboard") &&
                !(
                  settings?.hotel_housekeeping_enabled === false ||
                  settings?.hotel_housekeeping_enabled === "false"
                ),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.hotel.subItems.housekeepingTasks"),
                  url: "/hotel/housekeeping",
                  view: canPerform("access_housekeeping_dashboard"),
                },
                {
                  title: t("modules.hotel.subItems.housekeepingSupplies"),
                  url: "/hotel/housekeeping/supplies",
                  view: canPerform("view_housekeeping_supply_requests"),
                },
              ].filter((e) => e.view),
            },
            {
              // Conference and banquet events. Gated by `hotel_events_enabled`
              // as well as by permission: a property with no function space
              // should not carry a venue diary it can never use.
              //
              // One flat link, not a collapsible: venues are configuration
              // (Settings) and the event reports are listed with the other
              // hotel reports, so the group had one child left in it.
              title: t("modules.hotel.items.conferenceEvents"),
              url: "/hotel/events",
              icon: PartyPopper,
              view:
                canPerform("view_conference_events") &&
                (settings?.hotel_events_enabled === true ||
                  settings?.hotel_events_enabled === "true"),
            },
            {
              title: t("modules.hotel.items.reports"),
              url: "/hotel/reports",
              icon: BarChart3,
              view: canPerform("access_hotel_reports"),
            },
            {
              title: t("modules.hotel.items.settings"),
              url: "/hotel/settings",
              icon: Settings,
              view: canPerform("access_hotel_settings"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.hotel.subItems.generalSettings"),
                  url: "/hotel/settings",
                  view: canPerform("access_hotel_settings"),
                },
                {
                  title: t("modules.hotel.subItems.roomConfiguration"),
                  url: "/hotel/settings/room-configuration",
                  view: canPerform("access_hotel_settings"),
                },

                {
                  // Gated by `hotel_agents_enabled` — most properties sell
                  // direct and never pay a commission.
                  title: t("modules.hotel.subItems.agents"),
                  url: "/hotel/settings/agents",
                  view:
                    canPerform("view_hotel_agents") &&
                    (settings?.hotel_agents_enabled === true ||
                      settings?.hotel_agents_enabled === "true"),
                },
                {
                  title: t("modules.hotel.subItems.companies"),
                  url: "/hotel/settings/companies",
                  view: canPerform("view_hotel_companies"),
                },
                {
                  // Naming the function spaces is configuration, not diary
                  // work, so it lives with the other settings. Still gated by
                  // `hotel_events_enabled` like the events module itself.
                  title: t("modules.hotel.subItems.eventVenues"),
                  url: "/hotel/settings/event-venues",
                  view:
                    canPerform("view_conference_events") &&
                    (settings?.hotel_events_enabled === true ||
                      settings?.hotel_events_enabled === "true"),
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.facility.title"),
          url: "#",
          icon: Building2,
          isActive: pathname.startsWith("/facility"),
          module: "facility",
          items: [
            {
              title: t("modules.facility.items.overview"),
              url: "/facility/overview",
              icon: LayoutDashboard,
              view: canPerform("access_facility_dashboard"),
            },
            {
              title: t("modules.facility.items.bookings"),
              url: "/facility/bookings",
              icon: CalendarCheck,
              view: canPerform("view_facility_bookings"),
            },
            {
              title: t("modules.facility.items.calendar"),
              url: "/facility/calendar",
              icon: Calendar,
              view: canPerform("view_facility_calendar"),
            },
            {
              title: t("modules.facility.items.memberships"),
              url: "/facility/memberships",
              icon: Users,
              view: canPerform("view_facility_memberships"),
            },
            {
              title: t("modules.facility.items.guestManagement"),
              url: "/facility/guests",
              icon: UserRoundCheck,
              view:
                canPerform("view_facility_guests") ||
                canPerform("access_facility_guest_desk"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.facility.subItems.guests"),
                  url: "/facility/guests",
                  view: canPerform("view_facility_guests"),
                },
                {
                  title: t("modules.facility.subItems.guestDesk"),
                  url: "/facility/guest-desk",
                  view: canPerform("access_facility_guest_desk"),
                  isExternal: true,
                },
              ].filter((e) => e.view),
            },
            {
              title: t("modules.facility.items.enquiries"),
              url: "/facility/enquiries",
              icon: MessageSquareText,
              view: canPerform("view_facility_enquiries"),
            },
            {
              title: t("modules.facility.items.payments"),
              url: "/facility/payments",
              icon: CreditCard,
              view: canPerform("view_facility_payments"),
            },
            // {
            //   title: t("modules.facility.items.events"),
            //   url: "/facility/events",
            //   icon: Ticket,
            //   view: canPerform("view_facility_events"),
            // },
            {
              title: t("modules.facility.items.activities"),
              url: "/facility/activities",
              icon: Layers,
              view: canPerform("view_facility_activities"),
            },
            {
              title: t("modules.facility.items.settings"),
              url: "/facility/settings",
              icon: Settings,
              view: canPerform("view_facility_membership_plans"),
              isCollapsible: true,
              subItems: [
                {
                  title: t("modules.facility.subItems.generalSettings"),
                  url: "/facility/settings/general",
                  view: canPerform("manage_facility_settings"),
                },
                {
                  title: t("modules.facility.subItems.membershipPlans"),
                  url: "/facility/settings/membership-plans",
                  view: canPerform("view_facility_membership_plans"),
                },
                {
                  title: t("modules.facility.subItems.memberPortal"),
                  url: "/facility/settings/member-portal",
                  view: canPerform("manage_facility_settings"),
                },
              ].filter((e) => e.view),
            },
          ].filter((e) => e.view),
        },
        {
          title: t("modules.settings.title"),
          url: "#",
          icon: Settings,
          order: 100,
          isActive: pathname.startsWith("/settings"),
          alwaysShow: true, // Show regardless of modules - this is not a module
          items: [
            {
              title: t("modules.settings.items.generalInfo"),
              url: "/settings/general-information",
              icon: Building2,
              view: canPerform("manage_general_settings"),
            },
            {
              title: t("modules.settings.items.accountSettings"),
              url: "/settings/account-settings",
              icon: UserCog,
              view: canPerform("manage_account_settings"),
            },
            {
              title: t("modules.settings.items.systemUsers"),
              url: "/settings/system-users",
              icon: Users,
              view: canPerform("manage_system_users"),
            },
            {
              title: t("modules.settings.items.units"),
              url: "/settings/units",
              icon: Ruler,
              view: canPerform("view_units"),
            },
            {
              title: t("modules.settings.items.taxRates"),
              url: "/settings/tax-rates",
              icon: Receipt,
              view: canPerform("view_tax_rates"),
            },
            {
              title: t("modules.settings.items.paymentMethods"),
              url: "/settings/payment-methods",
              icon: CreditCard,
              view: canPerform("view_payment_methods"),
            },
            {
              title: t("modules.settings.items.integrations"),
              url: "/settings/third-party-integrations",
              icon: Plug,
              view: canPerform("manage_third_party_integrations"),
            },
            {
              title: t("modules.settings.items.rolesPermissions"),
              url: "/settings/roles-and-permissions",
              icon: Shield,
              view: canPerform("view_roles_and_permissions"),
            },
            {
              title: t("modules.settings.items.activityLogs"),
              url: "/settings/activity-logs",
              icon: History,
              view: canPerform("view_activity_logs"),
            },
            {
              title: t("modules.settings.items.billing"),
              url: "/settings/billing",
              icon: Receipt,
              view: canPerform("view_billing"),
            },
          ].filter((e) => e.view),
        },
      ]
        .filter((e) => e.items?.length)
        .filter((item) => {
          // Always show items with alwaysShow flag (like settings)
          if (item.alwaysShow) return true;
          return (
            enabledModules.some(
              (enabledModule) => enabledModule.moduleCode === item.module,
            ) || !item.module
          );
        })
        .map((e) => {
          const module = enabledModules.find(
            (enabledModule) => enabledModule.moduleCode === e.module,
          );
          return {
            ...e,
            order: e.order || module?.order,
          };
        })
        .sort((a, b) => a.order - b.order),
    };
  }, [
    activeOrg?.id,
    activeOrg?.name,
    canPerform,
    enabledModules,
    isIntegrationEnabled,
    isRestaurantOrderCompletionPaymentEnabled,
    isRestaurantDiscountsEnabled,
    orgDomain,
    organizations,
    pathname,
    role,
    settings,
    t,
    user,
  ]);

  const data = React.useMemo(() => {
    const query = search.toLowerCase();
    return {
      ...baseData,
      navMain: baseData.allNavMain
        .filter((item) => {
          if (query) {
            return (
              item.title.toLowerCase().includes(query) ||
              item.items?.some((subItem) =>
                subItem.title.toLowerCase().includes(query),
              )
            );
          }
          return true;
        })
        .map((item) => {
          if (item.items) {
            return {
              ...item,
              items: item.items.filter((subItem) => {
                if (query) {
                  return subItem.title.toLowerCase().includes(query);
                }
                return true;
              }),
            };
          }
          return item;
        }),
    };
  }, [baseData, search]);

  return {
    data,
    role,
    search,
    enabledModules,
    setSearch,
  };
}
